import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { find, map } from "lodash";
import { ParcelOrderList } from "Components/order-management/parcel-order-list";
import useSession from "Hooks/session.hook";
import { Button } from "@life-line-lab/components-react";
import { encode } from "Helpers/enc";
import { useHistory } from "react-router-dom";
import { ParcelStatusIssue } from "Data/order";
import { useOrderListingStates } from "Helpers/index";

const PendingReviewTab = ({
  baseColumns,
  handleRefreshData,
  handleModalAction,
}) => {
  const { t } = useTranslation();
  const { roleTypes, roles } = useSession();
  const [isViewOnly, setIsViewOnly] = useState<any>(true);
  const history = useHistory();
  const { selectedRows } = useOrderListingStates();

  const isInvalidRecipientAddress = (record: any) =>
    find(record.orderStatusInfo?.issueType, (typeObj) => {
      return (
        typeObj.name?.toUpperCase() ===
        ParcelStatusIssue.INVALID_RECIPIENT_ADDRESS
      );
    }) || false;

  const handleOpenParcelOrderEdit = (id: string) => {
    const currentUrl = new URL(
      location.pathname + location.search,
      window.location.origin,
    );

    const newUrl = new URL(
      `/parcels-order-processing/parcels/${id}/edit`,
      window.location.origin,
    );

    newUrl.searchParams.append(
      "from",
      encodeURIComponent(currentUrl.pathname + currentUrl.search),
    );

    history.push(newUrl.pathname + newUrl.search);
  };

  const additionalColumns = [
    {
      title: t("label.deliveryAttempts").toLocaleUpperCase(),
      dataIndex: ["returnInfo", "failedAttempt"],
      ellipsis: true,
      render: (text: any) => text ?? "0",
    },
    {
      title: t("label.issueType").toLocaleUpperCase(),
      dataIndex: ["orderStatusInfo"],
      width: 150,
      render: (orderStatusInfo: any) => {
        return (
          <span>
            {map(orderStatusInfo?.issueType, (issue) => {
              return (
                <span>
                  {issue?.name || <span>&#11835;</span>}
                  <br />
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      title: t("label.type").toLocaleUpperCase(),
      dataIndex: ["parcels", "name"],
      width: 80,
      render: (parcels: any) => {
        return (
          <span>
            {map(parcels, (parcel) => {
              return (
                <span>
                  {parcel?.name || <span>&#11835;</span>}
                  <br />
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      title: t("label.value").toLocaleUpperCase(),
      dataIndex: ["totalValueOfGoods"],
      width: 70,
    },
    {
      title: !isViewOnly ? t("label.action").toLocaleUpperCase() : "",
      dataIndex: [],
      width: 150,
      render: (_: any, record: any) => {
        return (
          <div>
            {!isViewOnly && !isInvalidRecipientAddress(record) ? (
              <div className="btn-actions-container">
                <Button
                  type="success"
                  label={t("action.confirm")}
                  onClick={() => {
                    const allOrderIDs = record.childrenOrderIDs
                      ? [record._id, ...record.childrenOrderIDs]
                      : [record._id];
                    handleModalAction("CONFIRM", allOrderIDs);
                  }}
                  className="approve-btn"
                />
                <Button
                  type="danger"
                  label={t("action.reject")}
                  onClick={() => {
                    const allOrderIDs = record.childrenOrderIDs
                      ? [record._id, ...record.childrenOrderIDs]
                      : [record._id];
                    handleModalAction(
                      "REJECT",
                      allOrderIDs,
                      record._id,
                      record.senderID,
                    );
                  }}
                  className="flag-btn"
                />
              </div>
            ) : (
              isInvalidRecipientAddress(record) && (
                <div className="btn-actions-container">
                  <Button
                    type="primary"
                    label={t("action.edit")}
                    onClick={() =>
                      handleOpenParcelOrderEdit(encode(record._id)!)
                    }
                    className="approve-btn"
                  />
                  <Button
                    type="danger"
                    label={t("action.reject")}
                    onClick={() => {
                      const allOrderIDs = record.childrenOrderIDs
                        ? [record._id, ...record.childrenOrderIDs]
                        : [record._id];
                      handleModalAction(
                        "REJECT",
                        allOrderIDs,
                        record._id,
                        record.senderID,
                      );
                    }}
                    className="flag-btn"
                  />
                </div>
              )
            )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (roles[0].type === "COMPANY" && roles[0].subType === "SUPER_ADMIN") {
      setIsViewOnly(false);
    } else {
      setIsViewOnly(true);
    }
  }, [roleTypes]);

  return (
    <ParcelOrderList
      columns={baseColumns.concat(additionalColumns)}
      additionalComponent={
        <div className="btn-actions-container">
          {roleTypes == "COMPANY-SUPER_ADMIN" && (
            <Button
              label={t("action.massConfirm")}
              type="primary"
              size="large"
              isDisabled={selectedRows.length == 0}
              onClick={() => handleModalAction("MASSCONFIRM")}
            />
          )}
        </div>
      }
      disableRowSelection={isInvalidRecipientAddress}
      isCheckBoxShown={true}
      filterItems={
        roles[0].type !== "CUSTOMER"
          ? ["deliveryType", "customersList"]
          : ["deliveryType"]
      }
      radioButtonComponent={undefined}
      handleRefreshData={handleRefreshData}
    />
  );
};

export default PendingReviewTab;
